import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { FiberStatusEnum, Fibra } from '../../../../models/fibra.model';
import { FibraService } from '../../../../services/fibra.service';
import { FormLayersModalComponent } from '../../dynamic-layers/form-layers-modal/form-layers-modal.component';

@Component({
  selector: 'ebw-monorepo-fiber-edit',
  templateUrl: './fiber-edit.component.html',
  styleUrls: ['./fiber-edit.component.scss']
})
export class FiberEditComponent implements OnInit {
  public fibra: Fibra;
  public form: FormGroup;
  public statoFibra = [
    {
      value: FiberStatusEnum.free,
      label: this._translateService.instant('map.sidebar.fibra.status.free')
    },
    {
      value: FiberStatusEnum.booked,
      label: this._translateService.instant('map.sidebar.fibra.status.booked')
    },
    {
      value: FiberStatusEnum.unavailable,
      label: this._translateService.instant('map.sidebar.fibra.status.unavailable')
    },
    {
      value: FiberStatusEnum.outOfOrder,
      label: this._translateService.instant('map.sidebar.fibra.status.out_of_order')
    },
    {
      value: FiberStatusEnum.bookedLD,
      label: this._translateService.instant('map.sidebar.fibra.status.booked_ld')
    },
    {
      value: FiberStatusEnum.unusable,
      label: this._translateService.instant('map.sidebar.fibra.status.unusable')
    },
    {
      value: FiberStatusEnum.toCheck,
      label: this._translateService.instant('map.sidebar.fibra.status.to_check')
    }
  ];

  constructor(
    public dialogRef: MatDialogRef<FormLayersModalComponent>,
    private _fibraService: FibraService,
    private _fb: FormBuilder,
    private _translateService: TranslateService,
    private _ebwModal: EbwModalService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  async ngOnInit(): Promise<void> {
    this._fetchData();
  }

  public async onSubmit() {
    if (this.form.invalid) {
      return;
    }

    let value: Fibra = this.form.getRawValue();
    const mappedValue: Fibra = { ...this.fibra, ...value, application: 'WEB' };

    this._save(mappedValue);
  }

  private _initForm(fibra: Fibra) {
    this.form = this._fb.group({
      stato: [fibra.stato, Validators.required],
      circuito_td: [fibra.circuito_td],
      codice_servizio_tecnico: [fibra.codice_servizio_tecnico],
      porte_apparato: [fibra.porte_apparato],
      porte_apparato_2: [fibra.porte_apparato_2],
      segment_rc: [fibra.segment_rc],
      note: [fibra.note],
      cliente: [fibra.cliente]
    });

    // application: "PNI" && stato IN ("OCCUPATA","PRENOTATA","BOOKED LD")
    if (
      fibra?.application === 'PNI' &&
      (fibra?.stato === FiberStatusEnum.unavailable || fibra?.stato === FiberStatusEnum.booked || fibra?.stato === FiberStatusEnum.bookedLD)
    ) {
      this.form.disable();
      this.form.get('porte_apparato').enable();
      this.form.get('porte_apparato_2').enable();
    }
  }

  private async _getLatestFibra(id) {
    try {
      const result = await firstValueFrom(this._fibraService.getFibra(id));

      return safeJsonClone(result);
    } catch (error) {
      return null;
    }
  }

  private async _fetchData() {
    const fibraTmp = await this._getLatestFibra(this.data.id);

    if (fibraTmp) {
      // FAS-1579: se il cavo è proprietario (potenzialità IRU = ND) la fibra non può diventare INUTILIZZABILE
      if (fibraTmp.cavo_potenzialita_iru === 'ND') {
        this.statoFibra = this.statoFibra.filter((stato) => stato.value !== FiberStatusEnum.unusable);
      }
      this.fibra = fibraTmp;
      this._initForm(fibraTmp);
    }
  }
  private _save(data: Fibra) {
    this._fibraService.updateFibra(data).subscribe({
      next: (resp) => {
        this._ebwModal
          .openSuccessModal({
            title: this._translateService.instant('common.save_successful'),
            content: ''
          })
          .afterClosed()
          .subscribe(() => {
            this.dialogRef.close(true);
          });
      },
      error: (error) => {
        const errorTitle = this._translateService.instant('common.error');
        const errorMessage = this._translateService.instant('common.error_saving');
        this._ebwModal.openErrorModal({ title: errorTitle, content: errorMessage });
      }
    });
  }
}
